import * as React from "react";

import { performOnNextFrame } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import {
  AppShyComponent,
  getShyComponentClassNames,
  PopOverState,
} from "@1js/acui-shy-component";
import { isReducedAnimations } from "@1js/window-util";

import { getShyRibbonStyles, ShyRibbonProps } from ".";
import {
  getRibbonBottomBarStylingConstants,
  getRibbonTopBarStylingConstants,
} from "../../ribbonStylingConstants";

export const hideComponentThresholdScrollLines = 60; // above this threshold, soft dismiss shy ribbon collapses
export const showComponentThresholdScrollLines = 100; // above this threshold, we expand the shy ribbon
export const shyRibbonIsActiveClassName = "ShyRibbonIsPopOver";

export interface ShyRibbonState {
  // buffered props. Need to delay when props flow to AppShyComponent to achieve ideal animations
  isActive: boolean;
  popoverState: PopOverState;

  /** Set to true when choreographing the transition to always show (active to inactive) */
  isDeactivating: boolean;

  /** Set to true when choreographing the transition to auto-hide (inactive to active + collapsed) */
  isActivating: boolean;

  /** Used in state machine for initially disabling popover until first collapse */
  disablePopOver: boolean;

  /** Set to true is animating between pop-over states */
  isAnimating: boolean;
}
export class ShyRibbon extends React.PureComponent<
  ShyRibbonProps,
  ShyRibbonState
> {
  private ignoreNextScrollEvent: boolean = false;
  private lastKnownScrollPosition: number = 0;
  private scrollActionsRegistered: boolean = false;
  private scrolledViaUserAction: boolean = false;
  private activeTouchInstance?: Touch;
  private animationPromise: Promise<void> | undefined = undefined;
  private animationPromiseResolver: (() => void) | undefined = undefined;
  private transitionStarted: boolean = false;
  private animatingDivRef = React.createRef<HTMLDivElement>();
  private deactivationAnimationPromise: Promise<void> | undefined = undefined;
  private deactivationAnimationPromiseResolver: (() => void) | undefined =
    undefined;

  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public static getDerivedStateFromProps(
    props: ShyRibbonProps,
    state: ShyRibbonState
  ) {
    const { isActive, popoverState } = props;

    // special handling for switching to inactive
    if (state.isActive && !isActive && !state.disablePopOver) {
      return {
        ...state,
        popoverState,
        isDeactivating: true,
      };
    }

    // special handling for switching to active if doing so and immediately collapsing (auto-hide)
    // this should NOT be the shy on boot scenario
    if (
      !state.isActive &&
      isActive &&
      popoverState === PopOverState.Collapsed
    ) {
      return {
        ...state,
        isActive: true,
        isActivating: true,
        popoverState: PopOverState.Expanded,
      };
    }

    // if we're still activating or deactivating, don't propagate props to state yet
    if (state.isActivating || state.isDeactivating) {
      return state;
    }

    if (
      isVisiblePopoverState(state.popoverState) !==
        isVisiblePopoverState(props.popoverState) &&
      !isReducedAnimations()
    ) {
      return {
        ...state,
        isAnimating: true,
        popoverState,
      };
    }

    // update state with latest props.
    return {
      ...state,
      isActive,
      popoverState,
    };
  }

  constructor(props: ShyRibbonProps) {
    super(props);
    const { isActive, disablePopOverOnBoot, popoverState } = props;
    this.state = {
      isActive,
      disablePopOver: !!disablePopOverOnBoot,
      popoverState,
      isDeactivating: false,
      isActivating: false,
      isAnimating: false,
    };
  }

  public render() {
    const { skipAnimation, children, triggerElement, shyDismissalRegions } =
      this.props;

    const getShyStyles = (disablePopover: boolean) => {
      return getShyRibbonStyles(
        this.getRibbonHeight() /** componentHeight */,
        !disablePopover /** usePopOver */,
        this.state.isAnimating /** isAnimating */,
        !!skipAnimation || isReducedAnimations() /** skipAnimations */
      );
    };

    return (
      <>
        <AppShyComponent
          isActive={this.state.isActive || this.state.isDeactivating}
          popOverState={this.state.popoverState}
          triggerElement={triggerElement}
          onPopOverStateChange={this.onShyRibbonStateChange}
          shyDismissalRegions={shyDismissalRegions}
          styles={getShyStyles(
            this.state.disablePopOver ||
              this.state.isActivating ||
              !!this.props.disablePopover
          )}
          onTransitionStart={this.onTransitionStart}
          onTransitionEnd={this.onTransitionEnd}
        >
          {children}
        </AppShyComponent>
        {
          // when transitioning to always show, this div animates the sliding of the canvas
          this.state.isDeactivating && !!!this.props.disablePopover ? (
            <div
              ref={this.animatingDivRef}
              className={
                getShyComponentClassNames(
                  !this.state.isActive /** isVisible */,
                  getShyStyles(true)
                ).container
              }
            />
          ) : undefined
        }
      </>
    );
  }

  public componentDidMount() {
    if (this.props.isActive) {
      this.registerActionsOnAnchor();
    }
  }

  public componentWillUnmount() {
    this.unregisterActionsOnAnchor();
  }

  public componentDidUpdate(
    prevProps: ShyRibbonProps,
    prevState: ShyRibbonState
  ) {
    const { popoverState, isActive } = this.props;
    const { isActivating, isDeactivating, disablePopOver, isAnimating } =
      this.state;

    // if our current state has changed from animating to finished animating,
    // then our transitions are complete (or we had animations turned off)
    // and should call back into the app.
    if (prevState.isAnimating && !isAnimating) {
      this.props.onAnimationFinished?.();
    }

    if (isActive) {
      this.registerActionsOnAnchor();
    } else {
      this.unregisterActionsOnAnchor();
    }

    // After we switch the deactivation div to relative positioning but before we change the height,
    // we will attach transitionEnd listener to allow us to hold off on stopping deactivation until we
    // are done shifting the content below the shy component
    if (!prevState.isDeactivating && isDeactivating && this.state.isActive) {
      this.animatingDivRef.current?.addEventListener(
        "transitionend",
        this.onDeactivateTransitionEnd
      );
    } else if (
      prevState.isDeactivating &&
      !isDeactivating &&
      !this.state.isActive
    ) {
      this.animatingDivRef.current?.removeEventListener(
        "transitionend",
        this.onDeactivateTransitionEnd
      );
    }

    // If the shy ribbon isn't animating anymore we can get rid of the promise
    // so the next time we start animating we create a new one and wait for that
    if (!isAnimating) {
      this.animationPromise = undefined;
    }

    const isPopOverAfterBoot =
      popoverState !== PopOverState.Collapsed && !disablePopOver && isActive;
    if (isPopOverAfterBoot) {
      this.setIsActiveClassName();
    } else if (
      (prevProps.popoverState !== PopOverState.Collapsed &&
        popoverState === PopOverState.Collapsed) ||
      !isActive
    ) {
      performOnNextFrame(() =>
        this.performAfterAnimate(() => this.removeIsActiveClassName())
      );
    }

    if (!prevState.isDeactivating && isDeactivating) {
      performOnNextFrame(() =>
        this.setState(
          { isActive: false },
          this.getSetStateAfterDeactivateAnimationCallback({
            isDeactivating: false,
          })
        )
      );
    }

    if (!prevState.isActivating && isActivating) {
      this.setState(
        { popoverState: PopOverState.Collapsed },
        this.getSetStateAfterAnimateCallback({ isActivating: false })
      );
    }

    // If we a users animations are off, we need to set the state to false as it won't happen in the Transition events.
    if (
      !prevState.isAnimating &&
      isAnimating &&
      (this.props.skipAnimation || isReducedAnimations())
    ) {
      performOnNextFrame(() =>
        this.setState({ popoverState, isAnimating: false })
      );
    }

    // disabling popover is only ever temporary. if popover disabled and collapsing, stop disabling popover after we've animated the collapse.
    if (
      this.state.disablePopOver &&
      popoverState === PopOverState.Collapsed &&
      prevProps.popoverState !== popoverState
    ) {
      this.getSetStateAfterAnimateCallback({ disablePopOver: false })();
    }

    // Notify listeners of shy behavior toggle
    if (
      prevProps.popoverState !== this.props.popoverState &&
      this.props.onNotifyRibbonStateListeners
    ) {
      this.props.onNotifyRibbonStateListeners(
        this.props.popoverState,
        prevProps.popoverState
      );
    }
  }

  /**
   * This function sets the active class name is the shy ribbon is in the pop-over state
   * This will allow other components to listen to it and decide if they should adjust their behavior.
   */
  private setIsActiveClassName = () => {
    if (!document.body.classList.contains(shyRibbonIsActiveClassName)) {
      document.body.classList.add(shyRibbonIsActiveClassName);
    }
  };

  private removeIsActiveClassName = () => {
    if (document.body.classList.contains(shyRibbonIsActiveClassName)) {
      document.body.classList.remove(shyRibbonIsActiveClassName);
    }
  };

  private registerActionsOnAnchor = () => {
    const { scrollableAnchorElement } = this.props;
    if (scrollableAnchorElement && !this.scrollActionsRegistered) {
      scrollableAnchorElement.addEventListener(
        "mousedown",
        this.collapsingElementTargeted
      );
      scrollableAnchorElement.addEventListener("touchstart", this.onTouchStart);
      scrollableAnchorElement.addEventListener("touchend", this.onTouchEnd);
      scrollableAnchorElement.addEventListener("touchmove", this.onTouchMove);
      scrollableAnchorElement.addEventListener("scroll", this.onRootScroll);
      scrollableAnchorElement.addEventListener("wheel", this.onWheel);
      window.addEventListener("resize", this.onResize);

      this.scrollActionsRegistered = true;
    }
  };

  private unregisterActionsOnAnchor = () => {
    const { scrollableAnchorElement } = this.props;
    if (scrollableAnchorElement && this.scrollActionsRegistered) {
      scrollableAnchorElement.removeEventListener(
        "mousedown",
        this.collapsingElementTargeted
      );
      scrollableAnchorElement.removeEventListener(
        "touchstart",
        this.onTouchStart
      );
      scrollableAnchorElement.removeEventListener("touchend", this.onTouchEnd);
      scrollableAnchorElement.removeEventListener(
        "touchmove",
        this.onTouchMove
      );
      scrollableAnchorElement.removeEventListener("scroll", this.onRootScroll);
      scrollableAnchorElement.removeEventListener("wheel", this.onWheel);
      window.removeEventListener("resize", this.onResize);

      this.scrollActionsRegistered = false;
    }
  };

  /**
   * When the browser window resizes, we may get a scroll event because the scrollable element
   * is adjusting to the new space it has.  In these cases, we don't want to do anything with that
   * event since it wasn't user initiated by user interaction directly on the element
   */
  private onResize = () => {
    this.ignoreNextScrollEvent = true;
  };

  /**
   * Scrolling on the anchor element is used to hide/show the shy component
   */
  private onRootScroll = (ev: Event) => {
    // If scrollTop is changed programatically, it fires a scroll event. This flag is used to prevent handling those cases as user scroll actions
    if (this.ignoreNextScrollEvent || !this.scrolledViaUserAction) {
      this.ignoreNextScrollEvent = false;
      return;
    }

    // if we have gotten this far, we will assume it was from a user action and we need to
    // reset the state so we don't effect the next event scroll event.
    this.scrolledViaUserAction = false;

    if (!ev.target) {
      return; // if we don't know the target, should we really expand/collapse?
    }

    const newScrollPosition = (ev.target as HTMLElement).scrollTop;

    if (
      newScrollPosition >
        this.lastKnownScrollPosition + hideComponentThresholdScrollLines &&
      this.ribbonIsVisible() &&
      this.isSoftDismissible()
    ) {
      // scrolling down hides shy components
      this.lastKnownScrollPosition = newScrollPosition;
      this.onShyRibbonStateChange(PopOverState.Collapsed);
      return;
    }
    if (
      newScrollPosition <
        this.lastKnownScrollPosition - showComponentThresholdScrollLines &&
      !this.ribbonIsVisible()
    ) {
      // scrolling up shows shy components
      this.lastKnownScrollPosition = newScrollPosition;
      this.onShyRibbonStateChange(PopOverState.ExpandedSoftDismiss);
      return;
    }
    this.lastKnownScrollPosition = newScrollPosition;
  };

  /**
   * When the shy component is visible, any interaction with the anchor element should dismiss the component
   */
  private collapsingElementTargeted = () => {
    if (this.ribbonIsVisible()) {
      this.onShyRibbonStateChange(PopOverState.Collapsed);
    }
  };

  private isSoftDismissible = (): boolean => {
    return this.props.popoverState === PopOverState.ExpandedSoftDismiss;
  };

  private onShyRibbonStateChange = (newState: PopOverState) => {
    if (!this.state.isAnimating) {
      if (newState === PopOverState.Collapsed) {
        this.hideShyRibbon();
      } else {
        this.showShyRibbon(newState);
      }
    }
  };

  private hideShyRibbon = () => {
    this.props.onStateChange?.(PopOverState.Collapsed, this.props.popoverState);
  };

  private showShyRibbon = (newState: PopOverState) => {
    this.props.onStateChange?.(newState, this.props.popoverState);
  };

  /**
   * Responsible for updating the last known scroll position of the scrollable element.
   *
   * Update should occur from the onWheel event, which occurs before the scrollableElement
   * receives the onScroll event.  This function will be either initializing the value for
   * the first time, since doing this during ComponentMount will cause a perf regression, or
   * it will update the current value because the user scrolled the canvas in a different
   * way (with keyboard, mouse on scroll bar, etc.)
   */
  private updatetLastKnownScrollPosition = () => {
    this.lastKnownScrollPosition =
      this.props.scrollableAnchorElement!.scrollTop;
  };

  private onWheel = (ev: WheelEvent) => {
    const { onStateChange, scrollableAnchorElement, isActive, popoverState } =
      this.props;

    this.updatetLastKnownScrollPosition();

    this.scrolledViaUserAction = true;
    if (
      !isActive ||
      this.ribbonIsVisible() ||
      scrollableAnchorElement!.scrollTop !== 0
    ) {
      return;
    }
    // If the user is already at top of document and then scrolls up, expand the ribbon
    // onWheel is attached to scrollableAnchorElement wheel event. If we're here, we can assume scrollableAnchorElement is set.
    if (scrollableAnchorElement!.scrollTop === 0 && ev.deltaY < 0) {
      onStateChange(PopOverState.ExpandedSoftDismiss, popoverState);
    }
  };

  private onTouchStart = (ev: TouchEvent) => {
    if (ev.touches.length === 1) {
      this.activeTouchInstance = ev.touches[0];
    } else {
      this.activeTouchInstance = undefined;
    }

    this.collapsingElementTargeted();
  };

  private onTouchEnd = () => {
    this.activeTouchInstance = undefined;
  };

  private onTouchMove = (ev: TouchEvent) => {
    const { onStateChange, scrollableAnchorElement, isActive, popoverState } =
      this.props;

    this.updatetLastKnownScrollPosition();

    this.scrolledViaUserAction = true;
    if (
      !isActive ||
      this.ribbonIsVisible() ||
      scrollableAnchorElement!.scrollTop !== 0 ||
      !this.activeTouchInstance ||
      ev.touches.length !== 1 ||
      ev.changedTouches.length !== 1
    ) {
      return;
    }

    // if the user is already at the top of the document, an attempt to scroll up using touch should expand the ribbon
    const delta = ev.changedTouches[0];
    if (
      this.activeTouchInstance.identifier === delta.identifier &&
      this.activeTouchInstance.clientY < delta.clientY
    ) {
      onStateChange(PopOverState.ExpandedSoftDismiss, popoverState);
    }
  };

  private ribbonIsVisible = (): boolean => {
    return (
      isVisiblePopoverState(this.props.popoverState) ||
      this.props.popoverState === PopOverState.Pinned
    );
  };

  private getRibbonHeight = () => {
    return this.props.shyBottomBarOnly
      ? getRibbonBottomBarStylingConstants(this.props.ribbonMode)
          .bottomBarHeight
      : getRibbonTopBarStylingConstants().topBarHeight +
          getRibbonBottomBarStylingConstants(this.props.ribbonMode)
            .bottomBarHeight;
  };

  private onTransitionStart = (ev: TransitionEvent) => {
    // Transition events will fire for all events because the base component is applying
    // this to all properties.  However, the for the Shy Ribbon, the 'height' property is
    // the only we care about since this will indicate if the Shy Ribbon component
    // is finished hiding or showing to the user.
    if (ev.propertyName === "height" && !this.transitionStarted) {
      this.transitionStarted = true;
    }
  };

  private onTransitionEnd = (ev: TransitionEvent) => {
    if (ev.propertyName === "height" && this.transitionStarted) {
      this.animationPromiseResolver?.();
      this.transitionStarted = false;

      // If we are done transitioning, we can set isAnimating to false.
      // This will also ensure that if any performAfterAnimation calls come in after
      // they will be executed immediately.
      this.setState({ isAnimating: false });
    }
  };

  private performAfterAnimate = (callback: () => void) => {
    if (this.props.skipAnimation || isReducedAnimations()) {
      callback();
    } else if (this.state.isAnimating) {
      if (this.animationPromise === undefined) {
        this.animationPromise = new Promise<void>((result) => {
          this.animationPromiseResolver = result;
        });
      }
      this.animationPromise
        .then(callback)
        .catch(() => (this.animationPromise = undefined));
    } else {
      this.animationPromise = undefined;
      callback();
    }
  };

  private onDeactivateTransitionEnd = (_ev: TransitionEvent) => {
    this.deactivationAnimationPromiseResolver?.();
    this.deactivationAnimationPromiseResolver = undefined;
    this.deactivationAnimationPromise = undefined;
  };

  private getSetStateAfterDeactivateAnimationCallback = (
    state: Pick<ShyRibbonState, Exclude<ShyRibbonState, {}>>
  ) => {
    return () =>
      this.performAfterDeactivationAnimationCallback(() =>
        this.setState(state)
      );
  };

  private performAfterDeactivationAnimationCallback = (
    callback: () => void
  ) => {
    if (this.props.skipAnimation || isReducedAnimations()) {
      callback();
    } else {
      if (this.deactivationAnimationPromise === undefined) {
        this.deactivationAnimationPromise = new Promise<void>((result) => {
          this.deactivationAnimationPromiseResolver = result;
        });
      }
      this.deactivationAnimationPromise
        .then(callback)
        .catch(() => (this.deactivationAnimationPromise = undefined));
    }
  };

  private getSetStateAfterAnimateCallback = (
    state: Pick<ShyRibbonState, Exclude<ShyRibbonState, {}>>
  ) => {
    return () => this.performAfterAnimate(() => this.setState(state));
  };
}

function isVisiblePopoverState(state: PopOverState) {
  return (
    state === PopOverState.Expanded ||
    state === PopOverState.ExpandedSoftDismiss
  );
}
